import { useLocalizationStore } from '$/components/core/Localization/hooks/useLocalizationStore';
import { envVars } from '$/envVars';
import {
  type CameraPosition,
  type InteriorStyle,
} from '$/services/usecases/projects/mapper/aiWorkflows';

export type NullableUrlSearchParams = Record<string, string | undefined>;

const getLanguage = () => useLocalizationStore.getState().language;

export const config = {
  BACKEND_URL: `${import.meta.env.VITE_BACKEND_URL}/api`,
};

export const imageEndpoints = {
  inspirationThumbnail: (id: string) =>
    `${envVars.VITE_BASE_STORAGE_URL}/spectrum-inspirations/${id}/thumbnail.jpg`,
  favoriteFolderThumbnail: (id: string) => {
    return `${envVars.VITE_BASE_STORAGE_URL}/spectrum-favoritefolderthumbnails/${id}.jpg`;
  },
};

export const backendEndpoints = {
  REGISTER_URL: `${config.BACKEND_URL}/signup`,
  VERIFY_USER_URL: `${config.BACKEND_URL}/signup/verify`,
  RESEND_VERIFICATION_URL: `${config.BACKEND_URL}/signup/resend`,
  SIGN_IN_URL: `${config.BACKEND_URL}/auth/signin`,
  SIGN_IN_REFRESH_URL: `${config.BACKEND_URL}/auth/refresh`,
  SAPCX_LOGIN_URL: `${config.BACKEND_URL}/auth/sapcx/login`,
  SAPCX_MIGRATE_URL: `${config.BACKEND_URL}/auth/sapcx/migrate`,
  SAPCX_CONSENT_URL: `${config.BACKEND_URL}/auth/sapcx/consent`,
  SAPCX_LINKING_URL: `${config.BACKEND_URL}/auth/sapcx/link`,
  UPLOAD_PROFILE_PICTURE: `${config.BACKEND_URL}/account/profilePicture`,
  SIGN_OUT_URL: `${config.BACKEND_URL}/auth/signout`,
  DELETE_ACCOUNT: `${config.BACKEND_URL}/account`,
  RESET_PASSWORD_INITIATE_URL: `${config.BACKEND_URL}/account/resetPassword/initiate`,
  RESET_PASSWORD_FULLFILL_URL: `${config.BACKEND_URL}/account/resetPassword/fullfill`,
  CHANGE_COUNTRY_URL: `${config.BACKEND_URL}/account/country`,
  FEEDBACK_URL: `${config.BACKEND_URL}/feedback`,
  COUNTRIES_URL: `${config.BACKEND_URL}/countries`,
  ADMIN_USERS_URL: `${config.BACKEND_URL}/admin/admins/countryAdmins`,
  ADMIN_KEY_USERS_URL: `${config.BACKEND_URL}/admin/keyUsers`,
  ADMIN_LANGUAGES_URL: `${config.BACKEND_URL}/admin/languages`,
  ADMIN_BRAND_COUNTRIES_URL: `${config.BACKEND_URL}/admin/brandCountries`,
  ADMIN_UPDATE_LANGUAGE_FILE_URL: `${config.BACKEND_URL}/admin/updateLanguageFile`,
  ADMIN_OPTIONS_URL: `${config.BACKEND_URL}/admin/adminOptions`,
  GEOLOCATION_URL: `${config.BACKEND_URL}/azure/geolocation`,
  PHOTOSERVICE_URL: `${config.BACKEND_URL}/photoservice`,
  INSPIRATION_URL: `${config.BACKEND_URL}/inspirations`,
  UPLOAD_INSPIRATION_THUMBNAIL_URL: (inspirationId: string) =>
    `${config.BACKEND_URL}/inspirations/${inspirationId}/thumbnail`,
  MATERIAL_URL: `${config.BACKEND_URL}/materials`,
  MATERIAL_FILTER_URL: `${config.BACKEND_URL}/materials/filter`,
  SCHOOLCLASSES_URL: `${config.BACKEND_URL}/schools/classes`,
  SCHOOL_STUDENTS_URL: `${config.BACKEND_URL}/schools/students`,
  SCHOOLS_URL: `${config.BACKEND_URL}/schools`,
  INVITATIONREQUESTS_URL: `${config.BACKEND_URL}/invitationRequests`,
  SCHOOLINVITATIONREQUESTS_URL: `${config.BACKEND_URL}/invitationRequests/schools`,
  ADMIN_AI_PROMPTS_URL: `${config.BACKEND_URL}/admin/aiPrompts`,
};

export const projectEndpoints = {
  PROJECT_OVERVIEW_URL: `${config.BACKEND_URL}/projects`,
  PROJECT_FOLDERS_URL: `${config.BACKEND_URL}/projectFolders`,
  SAVE_EDITOR_PROJECT_URL: `${config.BACKEND_URL}/projects/editor`,
  GET_PROJECT_URL: (
    type: 'editor' | 'inspiration' | 'legacy-inspiration',
    projectId: string,
  ) => `${config.BACKEND_URL}/projects/${type}/${projectId}`,
  ADD_PROJECT_ID_TO_EDITOR_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}`,
  UPSERT_INSPIRATION_EDITOR_PROJECT_URL: `${config.BACKEND_URL}/projects/inspiration`,
  UPLOAD_PROJECT_THUMBNAIL_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/${projectId}/thumbnail`,
  UPLOAD_PROJECT_VIEW_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/views`,
  RENAME_PROJECT_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/${projectId}/name`,
  DUPLICATE_PROJECT_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/${projectId}/duplicate`,
  DELETE_PROJECT_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/${projectId}`,
  CHANGE_PROJECT_VISIBILITY_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/${projectId}/visibility`,
  REFINE_EDITOR_PROJECT_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/refine`,
  REPROJECT_EDITOR_PROJECT_URL: (projectId: string) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/reproject`,
  AI_CORRECTION_EDITOR_PROJECT_URL: (projectId: string, viewIndex?: number) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/ai/correction${
      viewIndex != null ? `?viewIndex=${viewIndex}` : ''
    }`,
  AI_INTERIOR_STYLE_EDITOR_PROJECT_URL: (
    projectId: string,
    style: InteriorStyle,
    viewIndex?: number,
  ) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/ai/interior-style?style=${style}${viewIndex != null ? `&viewIndex=${viewIndex}` : ''}`,
  AI_REPROJECT_EDITOR_PROJECT_URL: (
    projectId: string,
    cameraPosition: CameraPosition,
    viewIndex?: number,
  ) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/ai/reproject?cameraPosition=${encodeURIComponent(cameraPosition)}${viewIndex != null ? `&viewIndex=${viewIndex}` : ''}`,
  AI_SEGMENTATION_EDITOR_PROJECT_URL: (projectId: string, viewIndex?: number) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/ai/segmentation${
      viewIndex != null ? `?viewIndex=${viewIndex}` : ''
    }`,
  AI_ASSISTANT_EDITOR_PROJECT_URL: (projectId: string, viewIndex?: number) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/ai/assistant${viewIndex != null ? `?viewIndex=${viewIndex}` : ''}`,
  AI_HARMONIES_EDITOR_PROJECT_URL: (projectId: string, viewIndex?: number) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/ai/harmonies${viewIndex != null ? `?viewIndex=${viewIndex}` : ''}`,
  AI_HARMONIES_RECOLOR_EDITOR_PROJECT_URL: (
    projectId: string,
    viewIndex?: number,
  ) =>
    `${config.BACKEND_URL}/projects/editor/${projectId}/ai/harmonies/recolor${viewIndex != null ? `?viewIndex=${viewIndex}` : ''}`,
};

export const favoritesEndpoints = {
  GET_FAVORITES_URL: `${config.BACKEND_URL}/favorites/material`,
  ADD_FAVORITE_URL: (materialId: string) =>
    `${config.BACKEND_URL}/favorites/material/${materialId}`,
  REMOVE_FAVORITE_URL: (materialId: string) =>
    `${config.BACKEND_URL}/favorites/material/${materialId}`,
  CHANGE_FAVORITE_FOLDERS_URL: (materialId: string) =>
    `${config.BACKEND_URL}/favorites/material/${materialId}`,
  ADD_FAVORITE_FOLDER_URL: `${config.BACKEND_URL}/favorites/folder`,
  REMOVE_FAVORITE_FOLDER_URL: (folderId: string) =>
    `${config.BACKEND_URL}/favorites/folder/${folderId}`,
  UPDATE_FAVORITE_FOLDER_URL: (folderId: string) =>
    `${config.BACKEND_URL}/favorites/folder/${folderId}`,
  CHANGE_GLOBAL_FAVORITE_FOLDER_URL: (folderId: string) =>
    `${config.BACKEND_URL}/favorites/global/${folderId}`,
  DUPLICATE_FAVORITE_FOLDER_URL: (folderId: string) =>
    `${config.BACKEND_URL}/favorites/folder/${folderId}/duplicate`,
};

export const legalEndpoints = {
  IMPRINT_URL: () => `${config.BACKEND_URL}/legal/imprint?lng=${getLanguage()}`,
  PRIVACY_URL: () => `${config.BACKEND_URL}/legal/privacy?lng=${getLanguage()}`,
  INTEGRITY_URL: () =>
    `${config.BACKEND_URL}/legal/integrity?lng=${getLanguage()}`,
  TOS_URL: () => `${config.BACKEND_URL}/legal/tos?lng=${getLanguage()}`,
};
